import { canInsertInlineIn, INLINE_EMOJI } from '@gitbook/doc-core';
import { TextRange, type Value } from '@gitbook/slate';
import type { InlineProps, MarkProps, Plugin } from '@gitbook/slate-react';

import { EmojiPickerMark } from './EmojiPickerMark';
import { InlineEmoji } from './InlineEmoji';

const EMOJI_CONTEXT_TYPE = 'emojiContext';
const CAPTURE_REGEX = /(^|\s):(\w*)$/;

/**
 * Slate plugin to detect user typing ":".
 */
export const emojiPlugin: Plugin = {
    renderMark,
    renderNode,
    decorateValue,
};

/**
 * Annotate where the user is typing ":"
 */
function decorateValue(value: Value): TextRange[] {
    const { selection } = value;

    const inputValue = getInput(value);
    const decorations: TextRange[] = [];

    if (inputValue !== null && TextRange.isTextRange(selection)) {
        decorations.push(
            TextRange.create({
                anchorKey: selection.startKey,
                anchorOffset: selection.startOffset - inputValue.length - 1,
                focusKey: selection.startKey,
                focusOffset: selection.startOffset,
                marks: [
                    {
                        type: EMOJI_CONTEXT_TYPE,
                        data: {
                            value: inputValue,
                        },
                    },
                ],
                isAtomic: true,
            })
        );
    }

    return decorations;
}

function renderMark(props: MarkProps) {
    const { mark } = props;

    switch (mark.type) {
        case EMOJI_CONTEXT_TYPE:
            return <EmojiPickerMark {...props} />;
    }
}

function renderNode(props: InlineProps) {
    const { node } = props;

    switch (node.type) {
        case INLINE_EMOJI:
            return <InlineEmoji {...props} />;
    }
}

function getInput(value: Value): string | null {
    if (!value.isCollapsed) {
        return null;
    }

    const { document, startText, startBlock, selection } = value;

    if (!TextRange.isTextRange(selection)) {
        return null;
    }
    // In some cases, like if the node that was selected gets deleted,
    // `startText` can be null.
    if (!startText || !startBlock) {
        return null;
    }

    if (!canInsertInlineIn({ document, block: startBlock }, INLINE_EMOJI)) {
        return null;
    }

    const { startOffset } = selection;
    const textBefore = startText.text.slice(0, startOffset);

    const result = CAPTURE_REGEX.exec(textBefore);

    return result == null ? null : result[2];
}
